import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAmplitude, useTrackOnMount } from '../../providers/AmplitudeProvider';
import { AmplitudeEvent } from '../../constants/amplitudeEvents';

interface WalkingBenefitsProps {
    onContinue: () => void;
    onBack: () => void;
    hasWalkingExperience: boolean;
}

const WalkingBenefits: React.FC<WalkingBenefitsProps> = ({ onContinue, onBack, hasWalkingExperience }) => {
    const { trackEvent } = useAmplitude();

    useTrackOnMount([
        { event: AmplitudeEvent.QUESTION_VIEWED, properties: { step_number: 4, screen_name: 'fitness_education' } },
    ]);

    const handleContinue = () => {
        trackEvent(AmplitudeEvent.QUESTION_ANSWERED, {
            step_number: 4,
            screen_name: 'fitness_education',
            benefit: 'easy_effective_fitness',
        });
        onContinue();
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="flex items-center px-4 py-[26px] border-b border-[#17171714]">
                <button
                    onClick={onBack}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-[#00000066]"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 flex justify-center mr-10">
                    <span className="font-poppins text-[24px] tracking-[-2px]">
                        Move <span className="font-semibold">Together</span>
                    </span>
                </div>
            </header>

            <div className="flex-1 flex flex-col items-center px-4 pt-16">
                <div className="mb-10">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.6727 27.5103L9.8514 24.4451C9.11981 21.7148 7.44819 20.7823 6.6241 17.5523C5.72409 14.0388 6.47252 9.31072 10.3491 8.272C12.7574 7.62668 14.19 9.72748 14.7766 11.9169C15.8499 15.9222 14.154 19.8965 15.1962 23.786L15.8243 26.1299C16.2052 27.5516 15.3603 29.015 13.9387 29.3959C12.5171 29.7768 11.0536 28.9319 10.6727 27.5103M32.6594 27.1404L31.838 24.0753C31.1064 21.3449 32.0879 19.7016 31.1865 16.4922C30.2092 12.9995 27.197 9.27904 23.3205 10.3178C20.9121 10.9631 20.7219 13.4987 21.3086 15.6881C22.3818 19.6935 25.8375 22.2874 26.8797 26.1768L27.5078 28.5208C27.8887 29.9424 29.3521 30.7873 30.7737 30.4064C32.1954 30.0255 33.0403 28.5621 32.6594 27.1404M26.4725 24.6571L31.6241 23.2768M9.63744 23.6466L14.789 22.2663" stroke="#369FFF" strokeWidth="2.66665" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <h1 className="font-poppins text-[26px]/[43px] font-semibold text-center mb-5">
                    {hasWalkingExperience
                        ? <>Walking is an easy and<br />effective fitness option!</>
                        : <>You're going to<br />crush this!</>
                    }
                </h1>

                {/* Description */}
                <div className="text-center">
                    <p className="text-[18px] text-[#9AA0A6] mb-1">
                        No need for expensive studios,<br />
                        extreme diets, or spending<br />
                        hours in the gym.
                    </p>
                    <p className="text-[18px] text-[#9AA0A6]">
                        It's an <span className="font-bold text-black">easy, gentle, and affordable</span><br />
                        way to stay fit and<br />
                        healthy.
                    </p>
                </div>
            </div>

            <div className="px-5 py-6 border-t border-[#F0F0F0] bg-white">
                <div className="max-w-md mx-auto">
                    <button
                        onClick={handleContinue}
                        className="w-full bg-[#369FFF] text-white py-4 rounded-full font-semibold text-[16px] uppercase tracking-wider hover:bg-blue-600 transition-colors"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WalkingBenefits;
